import { useEffect, useState } from 'react';
import useSWR from 'swr';
import { useParams, useNavigate } from 'react-router-dom';
import {
 Box,
 Button,
 Flex,
 FormControl,
 FormErrorMessage,
 FormLabel,
 Heading,
 Input,
 Modal,
 ModalOverlay,
 ModalContent,
 ModalHeader,
 ModalCloseButton,
 ModalBody,
 ModalFooter,
 SimpleGrid,
 Table,
 Thead,
 Tbody,
 Tr,
 Th,
 Td,
 Text,
 Textarea,
 Tooltip,
 useDisclosure,
 useToast,
} from '@chakra-ui/react';
import {
 FiFolder,
 FiFolderPlus,
 FiUpload,
 FiDownload,
 FiStar,
 FiFileText,
 FiLink,
 FiExternalLink,
} from 'react-icons/fi';
import baseService from '../../services/base';
import Loader from '../../components/Feedback/Loader';
import { MainContent, PageHeaderStructure } from '../../components/Layout';
import { AttentionModal } from '../../components/Modals';
import { Select } from '../../components/Input';
import Breadcrumbs from '../../components/Breadcrumbs';
import TableMenu from '../../components/Menu/TableMenu';
import FilePreviewModal from '../../components/Data/FilePreviewModal';
import { getFileIcon } from '../../helpers/getFileIcon';
import { decodificarNomeDeUpload } from '../../utils/nome-de-upload';
import { hostDoLink, isHttpsUrl } from '../../helpers/externalLink';

async function fetchData(url) {
 const response = await baseService.get(url);
 return response?.data;
}

// "Questionários" (virtual, montada de /document/virtual/questionnaires) e
// "Relatórios" (pasta de SISTEMA, is_system=true, criada sozinha no 1º
// upload de /document/reports) ficam pinadas na raiz via activeSpecial
// ('virtual' | 'reports') — nenhuma das duas entra no esquema de subpastas
// (sem id_parent_folder de verdade, não fazem sentido aninhadas).

const SORT_OPTIONS = [
 { value: 'name_asc', label: 'Nome (A–Z)' },
 { value: 'name_desc', label: 'Nome (Z–A)' },
 { value: 'date_desc', label: 'Mais recentes' },
 { value: 'date_asc', label: 'Mais antigos' },
];

function filterAndSort(items, term, sortBy, dateField) {
 const search = term.trim().toLowerCase();
 const nome = (item) => decodificarNomeDeUpload(item.name || '');
 const result = (items || []).filter(
  (item) => !search || nome(item).toLowerCase().includes(search)
 );
 const byName = (a, b) => nome(a).localeCompare(nome(b), 'pt-BR', { sensitivity: 'base' });
 const byDate = (a, b) => new Date(a[dateField]) - new Date(b[dateField]);
 const compare = {
  name_asc: byName,
  name_desc: (a, b) => byName(b, a),
  date_asc: byDate,
  date_desc: (a, b) => byDate(b, a),
 }[sortBy];
 return result.sort(compare);
}

function FolderCardIcon({ isSystem }) {
 return <FiFolder size={22} color={isSystem ? '#818A8F' : '#2F80ED'} />;
}

// Card branco com hover/drag, mesma linguagem visual usada no resto do
// domínio (bg #F5F6F7 no hover, #E6F1F8 quando é alvo de drop).
function Card({ children, onClick, draggable, onDragStart, onDragOver, onDrop, onDragLeave, isDropTarget, ...rest }) {
 return (
  <Flex
   direction='column'
   border='1px solid #E7E9EA'
   borderRadius='12px'
   p='16px'
   cursor={onClick ? 'pointer' : 'default'}
   position='relative'
   transition='background 0.15s, border-color 0.15s'
   _hover={{ bg: isDropTarget ? '#E6F1F8' : '#F9FAFA' }}
   bg={isDropTarget ? '#E6F1F8' : 'white'}
   borderColor={isDropTarget ? '#2F80ED' : '#E7E9EA'}
   onClick={onClick}
   draggable={draggable}
   onDragStart={onDragStart}
   onDragOver={onDragOver}
   onDrop={onDrop}
   onDragLeave={onDragLeave}
   {...rest}
  >
   {children}
  </Flex>
 );
}

// Link direto pra pasta/arquivo no app do cliente (gs-form-survey-ui). O
// backend escopa: só quem é da empresa dona da pasta consegue abrir.
function clientDocumentsUrl(idFolder, idMedia) {
 const base = (process.env.REACT_APP_FORM_URL || '').replace(/\/$/, '');
 const params = new URLSearchParams({ pasta: String(idFolder) });
 if (idMedia) params.set('arquivo', String(idMedia));
 return `${base}/documentacao?${params.toString()}`;
}

// EG-70 — botão de link externo (ex.: pasta do Drive) dentro da pasta.
function LinkModal({ isOpen, onClose, idFolder, link, onSaved }) {
 const toast = useToast();
 const [label, setLabel] = useState('');
 const [url, setUrl] = useState('');
 const [errors, setErrors] = useState({});
 const [isSaving, setIsSaving] = useState(false);

 useEffect(() => {
  if (!isOpen) return;
  setLabel(link?.label || '');
  setUrl(link?.url || '');
  setErrors({});
 }, [isOpen, link]);

 async function handleSubmit() {
  const next = {};
  if (!label.trim()) next.label = 'Informe o texto do botão';
  if (!isHttpsUrl(url.trim())) next.url = 'Informe um link que comece com https://';
  setErrors(next);
  if (Object.keys(next).length) return;

  setIsSaving(true);
  try {
   const payload = { label: label.trim(), url: url.trim() };
   if (link) {
    await baseService.patch(`/document/folders/${idFolder}/links/${link.id_document_link}`, payload);
   } else {
    await baseService.post(`/document/folders/${idFolder}/links`, payload);
   }
   toast({ title: link ? 'Link atualizado' : 'Link criado', status: 'success' });
   onSaved();
   onClose();
  } catch (error) {
   toast({
    title: 'Não foi possível salvar o link',
    description: error?.response?.data?.message,
    status: 'error',
   });
  } finally {
   setIsSaving(false);
  }
 }

 return (
  <Modal isOpen={isOpen} onClose={onClose} isCentered>
   <ModalOverlay />
   <ModalContent>
    <ModalHeader>{link ? 'Editar link' : 'Adicionar link'}</ModalHeader>
    <ModalCloseButton />
    <ModalBody>
     <FormControl isInvalid={!!errors.label} mb='16px'>
      <FormLabel fontSize='sm'>Texto do botão</FormLabel>
      <Input
       placeholder='Ex.: Abrir pasta no Drive'
       maxLength={80}
       value={label}
       onChange={(e) => setLabel(e.target.value)}
      />
      <FormErrorMessage>{errors.label}</FormErrorMessage>
     </FormControl>
     <FormControl isInvalid={!!errors.url}>
      <FormLabel fontSize='sm'>Link</FormLabel>
      <Input
       placeholder='https://drive.google.com/...'
       value={url}
       onChange={(e) => setUrl(e.target.value)}
      />
      <FormErrorMessage>{errors.url}</FormErrorMessage>
      <Text fontSize='xs' color='#818A8F' mt='6px'>
       Quem acessa o destino é definido no próprio link (ex.: no Drive, "qualquer pessoa com o link").
      </Text>
     </FormControl>
    </ModalBody>
    <ModalFooter gap='8px'>
     <Button variant='ghost' onClick={onClose}>
      Cancelar
     </Button>
     <Button variant='primary' onClick={handleSubmit} isLoading={isSaving}>
      Salvar
     </Button>
    </ModalFooter>
   </ModalContent>
  </Modal>
 );
}

function FolderModal({ isOpen, onClose, idCompanyGroup, folder, parentId, onSaved }) {
 const toast = useToast();
 const [name, setName] = useState(folder?.name || '');
 const [description, setDescription] = useState(folder?.description || '');
 const [isSaving, setIsSaving] = useState(false);

 async function handleSubmit() {
  if (!name.trim()) {
   toast({ title: 'Digite o nome da pasta', status: 'warning' });
   return;
  }
  setIsSaving(true);
  try {
   if (folder) {
    await baseService.patch(`/document/folders/${folder.id_document_folder}`, {
     name,
     description,
    });
   } else {
    await baseService.post('/document/folders', {
     // Só manda id_company_group pra pasta RAIZ (sem pai) — dentro de um pai
     // (inclusive Documentos Gerais), o backend herda dele, ignora esse campo.
     id_company_group: parentId ? undefined : Number(idCompanyGroup),
     id_parent_folder: parentId || undefined,
     name,
     description,
    });
   }
   toast({ title: 'Pasta salva', status: 'success' });
   onSaved();
   onClose();
  } catch (error) {
   toast({
    title: 'Não foi possível salvar a pasta',
    description: error?.response?.data?.message,
    status: 'error',
   });
  } finally {
   setIsSaving(false);
  }
 }

 return (
  <Modal isOpen={isOpen} onClose={onClose}>
   <ModalOverlay />
   <ModalContent>
    <ModalHeader>{folder ? 'Editar pasta' : 'Nova pasta'}</ModalHeader>
    <ModalCloseButton />
    <ModalBody>
     <Text fontSize='sm' fontWeight={600} mb='6px'>
      Nome
     </Text>
     <Input value={name} onChange={(e) => setName(e.target.value)} mb='16px' />
     <Text fontSize='sm' fontWeight={600} mb='6px'>
      Descrição
     </Text>
     <Textarea value={description} onChange={(e) => setDescription(e.target.value)} />
    </ModalBody>
    <ModalFooter>
     <Button variant='outline' mr='12px' onClick={onClose}>
      Cancelar
     </Button>
     <Button variant='primary' onClick={handleSubmit} isLoading={isSaving}>
      Salvar
     </Button>
    </ModalFooter>
   </ModalContent>
  </Modal>
 );
}

// EG-70 (Fase 6/9) — upload de relatório vinculado a questionário/certificação,
// com modo de reuso (Fase 9): reaproveita uma mídia JÁ enviada (mesmo arquivo
// no S3) pra outro questionário, sem novo upload — POST /reports/link-existing
// em vez de POST /reports.
// Sem FilterCombobox de propósito: ele usa Portal (lição já registrada —
// Portal dentro de Modal foge da trava de foco do Chakra e fica inclicável).
// Select comum resolve, mesmo padrão dos outros modais deste domínio.
function ReportUploadModal({
 isOpen,
 onClose,
 idCompanyGroup,
 surveys,
 certificates,
 existingReports,
 onUploaded,
}) {
 const toast = useToast();
 const [mode, setMode] = useState('new'); // 'new' | 'existing'
 const [kind, setKind] = useState('survey'); // 'survey' | 'certificate'
 const [targetId, setTargetId] = useState('');
 const [file, setFile] = useState(null);
 const [existingMediaId, setExistingMediaId] = useState('');
 const [isSaving, setIsSaving] = useState(false);

 const options = (kind === 'survey' ? surveys : certificates)?.map((item) => ({
  value: String(kind === 'survey' ? item.id_survey : item.id),
  label: item.description || item.name,
 }));

 // A mesma mídia pode aparecer mais de uma vez em existingReports (1 linha
 // por vínculo, Fase 9) — dedupe por id_media pro select não repetir opção.
 const existingMediaOptions = Object.values(
  (existingReports || []).reduce((acc, item) => {
   acc[item.id_media] = { value: String(item.id_media), label: decodificarNomeDeUpload(item.name) };
   return acc;
  }, {})
 );

 function handleClose() {
  setMode('new');
  setKind('survey');
  setTargetId('');
  setFile(null);
  setExistingMediaId('');
  onClose();
 }

 async function handleSubmit() {
  if (!targetId) {
   toast({ title: 'Escolha o questionário ou a certificação', status: 'warning' });
   return;
  }
  if (mode === 'new' && !file) {
   toast({ title: 'Escolha o arquivo do relatório', status: 'warning' });
   return;
  }
  if (mode === 'existing' && !existingMediaId) {
   toast({ title: 'Escolha o documento já enviado', status: 'warning' });
   return;
  }
  setIsSaving(true);
  try {
   if (mode === 'new') {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('id_company_group', String(idCompanyGroup));
    formData.append(kind === 'survey' ? 'id_survey' : 'id_certificate', targetId);
    await baseService.post('/document/reports', formData, {
     headers: { 'Content-Type': 'multipart/form-data' },
    });
   } else {
    await baseService.post('/document/reports/link-existing', {
     id_company_group: Number(idCompanyGroup),
     id_media: Number(existingMediaId),
     [kind === 'survey' ? 'id_survey' : 'id_certificate']: Number(targetId),
    });
   }
   toast({ title: 'Relatório enviado', status: 'success' });
   onUploaded();
   handleClose();
  } catch (error) {
   toast({
    title: 'Não foi possível enviar o relatório',
    description: error?.response?.data?.message,
    status: 'error',
   });
  } finally {
   setIsSaving(false);
  }
 }

 return (
  <Modal isOpen={isOpen} onClose={handleClose}>
   <ModalOverlay />
   <ModalContent>
    <ModalHeader>Enviar relatório</ModalHeader>
    <ModalCloseButton />
    <ModalBody>
     <Text fontSize='sm' fontWeight={600} mb='6px'>
      Origem do arquivo
     </Text>
     <Select
      name='reportMode'
      mb='16px'
      value={mode}
      onChange={(e) => {
       setMode(e.target.value);
       setFile(null);
       setExistingMediaId('');
      }}
      options={[
       { value: 'new', label: 'Enviar novo arquivo' },
       { value: 'existing', label: 'Reaproveitar documento já enviado' },
      ]}
     />

     <Text fontSize='sm' fontWeight={600} mb='6px'>
      Vincular a
     </Text>
     <Select
      name='reportKind'
      mb='16px'
      value={kind}
      onChange={(e) => {
       setKind(e.target.value);
       setTargetId('');
      }}
      options={[
       { value: 'survey', label: 'Questionário' },
       { value: 'certificate', label: 'Certificação' },
      ]}
     />

     <Text fontSize='sm' fontWeight={600} mb='6px'>
      {kind === 'survey' ? 'Questionário' : 'Certificação'}
     </Text>
     <Select
      name='reportTarget'
      mb='16px'
      placeholder='Selecione'
      value={targetId}
      onChange={(e) => setTargetId(e.target.value)}
      options={options || []}
     />

     {mode === 'new' ? (
      <>
       <Text fontSize='sm' fontWeight={600} mb='6px'>
        Arquivo
       </Text>
       <Input type='file' onChange={(e) => setFile(e.target.files?.[0] || null)} />
      </>
     ) : (
      <>
       <Text fontSize='sm' fontWeight={600} mb='6px'>
        Documento já enviado
       </Text>
       <Select
        name='existingMedia'
        placeholder='Selecione'
        value={existingMediaId}
        onChange={(e) => setExistingMediaId(e.target.value)}
        options={existingMediaOptions}
       />
       {existingMediaOptions.length === 0 && (
        <Text fontSize='xs' color='#818A8F' mt='6px'>
         Nenhum relatório enviado ainda pra reaproveitar.
        </Text>
       )}
      </>
     )}
    </ModalBody>
    <ModalFooter>
     <Button variant='outline' mr='12px' onClick={handleClose}>
      Cancelar
     </Button>
     <Button variant='primary' onClick={handleSubmit} isLoading={isSaving}>
      Enviar
     </Button>
    </ModalFooter>
   </ModalContent>
  </Modal>
 );
}

// Pacote 5, generalizado (Lucas 21/09) — reaproveitar documento já enviado
// deixa de ser só de Relatórios: mesmo padrão do ReportUploadModal (modo
// "novo" vs "existente"), mas genérico pra qualquer pasta. GET /document/media
// alimenta o picker (todo mundo do espaço, todas as pastas); POST
// .../media/:idMedia/attach (Fase 1, já existia) efetiva o reuso.
function UploadOrReuseModal({ isOpen, onClose, idFolder, spaceCompanyGroupId, onUploaded }) {
 const toast = useToast();
 const [mode, setMode] = useState('new'); // 'new' | 'existing'
 const [file, setFile] = useState(null);
 const [existingMediaId, setExistingMediaId] = useState('');
 const [searchTerm, setSearchTerm] = useState('');
 const [isSaving, setIsSaving] = useState(false);

 // Sem debounce de propósito — lista pequena (reaproveitar é sempre dentro
 // de um único espaço + os públicos), refetch a cada tecla não pesa.
 const searchQuery = [
  spaceCompanyGroupId ? `id_company_group=${spaceCompanyGroupId}` : null,
  searchTerm.trim() ? `search=${encodeURIComponent(searchTerm.trim())}` : null,
 ]
  .filter(Boolean)
  .join('&');
 const { data: existingMedia } = useSWR(
  isOpen && mode === 'existing' ? `/document/media${searchQuery ? `?${searchQuery}` : ''}` : null,
  fetchData
 );
 const existingMediaOptions = (existingMedia || []).map((m) => ({
  value: String(m.id_media),
  label: decodificarNomeDeUpload(m.name),
 }));

 function handleClose() {
  setMode('new');
  setFile(null);
  setExistingMediaId('');
  setSearchTerm('');
  onClose();
 }

 async function handleSubmit() {
  if (mode === 'new' && !file) {
   toast({ title: 'Escolha o arquivo', status: 'warning' });
   return;
  }
  if (mode === 'existing' && !existingMediaId) {
   toast({ title: 'Escolha o documento já enviado', status: 'warning' });
   return;
  }
  setIsSaving(true);
  try {
   if (mode === 'new') {
    const formData = new FormData();
    formData.append('file', file);
    await baseService.post(`/document/folders/${idFolder}/media`, formData, {
     headers: { 'Content-Type': 'multipart/form-data' },
    });
   } else {
    await baseService.post(`/document/folders/${idFolder}/media/${existingMediaId}/attach`);
   }
   toast({ title: 'Arquivo enviado', status: 'success' });
   onUploaded();
   handleClose();
  } catch (error) {
   toast({
    title: 'Não foi possível enviar o arquivo',
    description: error?.response?.data?.message,
    status: 'error',
   });
  } finally {
   setIsSaving(false);
  }
 }

 return (
  <Modal isOpen={isOpen} onClose={handleClose}>
   <ModalOverlay />
   <ModalContent>
    <ModalHeader>Enviar arquivo</ModalHeader>
    <ModalCloseButton />
    <ModalBody>
     <Text fontSize='sm' fontWeight={600} mb='6px'>
      Origem do arquivo
     </Text>
     <Select
      name='uploadMode'
      mb='16px'
      value={mode}
      onChange={(e) => {
       setMode(e.target.value);
       setFile(null);
       setExistingMediaId('');
       setSearchTerm('');
      }}
      options={[
       { value: 'new', label: 'Enviar novo arquivo' },
       { value: 'existing', label: 'Reaproveitar documento já enviado' },
      ]}
     />

     {mode === 'new' ? (
      <>
       <Text fontSize='sm' fontWeight={600} mb='6px'>
        Arquivo
       </Text>
       <Input type='file' onChange={(e) => setFile(e.target.files?.[0] || null)} />
      </>
     ) : (
      <>
       <Text fontSize='sm' fontWeight={600} mb='6px'>
        Buscar por nome
       </Text>
       <Input
        placeholder='Filtrar documentos já enviados'
        mb='12px'
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
       />
       <Text fontSize='sm' fontWeight={600} mb='6px'>
        Documento já enviado
       </Text>
       <Select
        name='existingMediaGeneric'
        placeholder='Selecione'
        value={existingMediaId}
        onChange={(e) => setExistingMediaId(e.target.value)}
        options={existingMediaOptions}
       />
       {existingMediaOptions.length === 0 && (
        <Text fontSize='xs' color='#818A8F' mt='6px'>
         Nenhum documento encontrado.
        </Text>
       )}
      </>
     )}
    </ModalBody>
    <ModalFooter>
     <Button variant='outline' mr='12px' onClick={handleClose}>
      Cancelar
     </Button>
     <Button variant='primary' onClick={handleSubmit} isLoading={isSaving}>
      Enviar
     </Button>
    </ModalFooter>
   </ModalContent>
  </Modal>
 );
}

export default function DocumentFolders({ isGlobal = false }) {
 const { idCompanyGroup } = useParams();
 const spaceCompanyGroupId = isGlobal ? null : idCompanyGroup;
 const navigate = useNavigate();
 const toast = useToast();

 // Navegação por nível: currentFolderId null = raiz. activeSpecial só existe
 // na raiz (Questionários/Relatórios continuam pinados lá, sem entrar no
 // esquema de subpastas).
 const [currentFolderId, setCurrentFolderId] = useState(null);
 const [activeSpecial, setActiveSpecial] = useState(null); // 'virtual' | 'reports' | null
 const [editingFolder, setEditingFolder] = useState(null);
 const [deleteTarget, setDeleteTarget] = useState(null); // { type: 'folder'|'media'|'link', id }
 const [editingLink, setEditingLink] = useState(null);
 const [isUploading, setIsUploading] = useState(false);
 const [previewMedia, setPreviewMedia] = useState(null);
 const [draggedItem, setDraggedItem] = useState(null); // { type: 'folder'|'media', id }
 const [dropTarget, setDropTarget] = useState(null); // id_document_folder sob o cursor do drag
 const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
 const [nameSearch, setNameSearch] = useState('');
 const [sortBy, setSortBy] = useState('name_asc');
 const folderModal = useDisclosure();
 const reportModal = useDisclosure();
 const previewModal = useDisclosure();
 const uploadModal = useDisclosure();
 const linkModal = useDisclosure();

 const isVirtual = activeSpecial === 'virtual';
 const isReports = activeSpecial === 'reports';
 // "Documentos Gerais" não tem uma tela de raiz própria (ela É a raiz de
 // um espaço sem dono) — o mount já entra direto nela, ver efeito abaixo.
 const isRoot = !isGlobal && currentFolderId === null && !activeSpecial;
 const isFolderView = currentFolderId !== null;

 // Singleton (Lucas, 21/09) — qualquer autenticado lê, sem precisar de
 // id_company_group (não existe um, é esse o ponto da pasta geral).
 const { data: globalFolder } = useSWR(isGlobal ? '/document/global-folder' : null, fetchData);

 useEffect(() => {
  if (isGlobal && globalFolder && currentFolderId === null) {
   setCurrentFolderId(globalFolder.id_document_folder);
  }
 }, [isGlobal, globalFolder, currentFolderId]);

 // Com id_parent_folder informado o backend resolve o id_company_group pela
 // PRÓPRIA pasta-mãe (ignora a query) — então não precisa (nem dá, no modo
 // global) mandar id_company_group quando já se está dentro de uma pasta.
 const { data: folders, isLoading: loadingFolders, mutate: mutateFolders } = useSWR(
  !activeSpecial && (currentFolderId || !isGlobal)
   ? currentFolderId
     ? `/document/folders?id_parent_folder=${currentFolderId}`
     : `/document/folders?id_company_group=${idCompanyGroup}`
   : null,
  fetchData
 );

 const { data: breadcrumbPath } = useSWR(
  currentFolderId ? `/document/folders/${currentFolderId}/path` : null,
  fetchData
 );
 const currentFolderData = breadcrumbPath?.[breadcrumbPath.length - 1];

 // EG-70 (Fase 5) — "Destacados": GET /document/favorites já vem juntado com
 // nome/id_company_group (backend resolve, ver document.service.ts). Filtra
 // pelo grupo aberto porque o endpoint devolve TODOS os favoritos do usuário
 // logado, sem filtro por grupo (um MASTER navega entre vários). No modo
 // global o "grupo" é null (mesma convenção da pasta/mídia de verdade).
 const { data: favorites, mutate: mutateFavorites } = useSWR('/document/favorites', fetchData);
 const groupFavorites = (favorites || []).filter((f) =>
  isGlobal ? f.id_company_group === null : Number(f.id_company_group) === Number(idCompanyGroup)
 );
 const favoritedFolderIds = new Set(
  groupFavorites.filter((f) => f.entity_type === 'folder').map((f) => f.entity_id)
 );
 const favoritedMediaIds = new Set(
  groupFavorites.filter((f) => f.entity_type === 'media').map((f) => f.entity_id)
 );

 // Só busca quando a view de Relatórios está aberta — surveys/certificates
 // são listas globais (sem filtro por grupo no backend hoje), usadas só pra
 // montar o seletor do modal e resolver o nome do vínculo na tabela.
 const { data: reports, isLoading: loadingReports, mutate: mutateReports } = useSWR(
  isReports ? `/document/reports?id_company_group=${idCompanyGroup}` : null,
  fetchData
 );
 const { data: surveys } = useSWR(isReports ? '/survey' : null, fetchData);
 const { data: certificates } = useSWR(isReports ? '/certificate' : null, fetchData);

 function resolveReportLink(item) {
  if (item.id_survey) {
   const survey = surveys?.find((s) => s.id_survey === item.id_survey);
   return survey?.description || `Questionário #${item.id_survey}`;
  }
  if (item.id_certificate) {
   const certificate = certificates?.find((c) => c.id === item.id_certificate);
   return certificate?.name || `Certificação #${item.id_certificate}`;
  }
  return '—';
 }

 const { data: media, isLoading: loadingMedia, mutate: mutateMedia } = useSWR(
  isFolderView ? `/document/folders/${currentFolderId}/media` : null,
  fetchData
 );

 const { data: links, mutate: mutateLinks } = useSWR(
  isFolderView ? `/document/folders/${currentFolderId}/links` : null,
  fetchData
 );

 const { data: virtualFiles, isLoading: loadingVirtual } = useSWR(
  isVirtual ? `/document/virtual/questionnaires?id_company_group=${idCompanyGroup}` : null,
  fetchData
 );

 function openRoot() {
  setCurrentFolderId(null);
  setActiveSpecial(null);
 }

 function openFolder(id) {
  setCurrentFolderId(id);
  setActiveSpecial(null);
 }

 function openVirtual() {
  setCurrentFolderId(null);
  setActiveSpecial('virtual');
 }

 function openReports() {
  setCurrentFolderId(null);
  setActiveSpecial('reports');
 }

 async function uploadFile(file) {
  setIsUploading(true);
  try {
   const formData = new FormData();
   formData.append('file', file);
   await baseService.post(`/document/folders/${currentFolderId}/media`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
   });
   toast({ title: 'Arquivo enviado', status: 'success' });
   mutateMedia();
   mutateFolders();
  } catch (error) {
   toast({
    title: 'Não foi possível enviar o arquivo',
    description: error?.response?.data?.message,
    status: 'error',
   });
  } finally {
   setIsUploading(false);
  }
 }

 async function handleDownload(idMedia) {
  const { data } = await baseService.get(`/document/media/${idMedia}/download-url`);
  window.open(data.url, '_blank');
 }

 async function handleDownloadVirtual(idFile) {
  const { data } = await baseService.get(
   `/document/virtual/questionnaires/${idFile}/download-url?id_company_group=${idCompanyGroup}`
  );
  window.open(data.url, '_blank');
 }

 async function confirmDelete() {
  if (!deleteTarget) return;
  try {
   if (deleteTarget.type === 'folder') {
    await baseService.delete(`/document/folders/${deleteTarget.id}`);
    // Excluiu a pasta que estava ABERTA: sobe pro pai dela, não pra raiz
    // absoluta (senão excluir uma subpasta funda te joga lá embaixo).
    if (currentFolderId === deleteTarget.id) {
     const parentId = currentFolderData?.id_parent_folder ?? null;
     if (parentId) openFolder(parentId);
     else openRoot();
    }
    mutateFolders();
   } else if (deleteTarget.type === 'link') {
    await baseService.delete(`/document/folders/${currentFolderId}/links/${deleteTarget.id}`);
    mutateLinks();
   } else {
    await baseService.delete(`/document/folders/${currentFolderId}/media/${deleteTarget.id}`);
    mutateMedia();
    mutateFolders();
   }
   toast({ title: 'Excluído com sucesso', status: 'success' });
  } catch (error) {
   toast({
    title: 'Não foi possível excluir',
    description: error?.response?.data?.message,
    status: 'error',
   });
  } finally {
   setDeleteTarget(null);
  }
 }

 async function copyClientLink(idFolder, idMedia) {
  try {
   await navigator.clipboard.writeText(clientDocumentsUrl(idFolder, idMedia));
   toast({
    title: 'Link copiado',
    description: 'Só usuários da empresa dona da pasta conseguem abrir.',
    status: 'success',
   });
  } catch {
   toast({ title: 'Não foi possível copiar o link', status: 'error' });
  }
 }

 function openLinkModal(link = null) {
  setEditingLink(link);
  linkModal.onOpen();
 }

 async function toggleFavorite(entityType, entityId) {
  await baseService.post('/document/favorites/toggle', {
   entity_type: entityType,
   entity_id: entityId,
  });
  mutateFavorites();
 }

 // Drag-and-drop nativo (HTML5) — arrastar um card (pasta ou arquivo) e
 // soltar em cima de outra pasta (ou de um item do breadcrumb) move; sem
 // lib nova de propósito (ver plano). Um item por vez, sem multi-seleção.
 function handleDragStart(e, item) {
  setDraggedItem(item);
  e.dataTransfer.effectAllowed = 'move';
 }

 function handleDragEnd() {
  setDraggedItem(null);
  setDropTarget(null);
 }

 function handleFolderDragOver(e, folder) {
  if (!draggedItem) return; // deixa passar o dragover nativo de arquivo do SO
  if (draggedItem.type === 'folder' && draggedItem.id === folder.id_document_folder) return;
  e.preventDefault();
  setDropTarget(folder.id_document_folder);
 }

 async function handleFolderDrop(e, folder) {
  e.preventDefault();
  setDropTarget(null);
  if (!draggedItem) return;
  await moveItem(draggedItem, folder.id_document_folder);
  setDraggedItem(null);
 }

 function handleBreadcrumbDragOver(e, item) {
  if (!draggedItem || item.isDroppable === false) return;
  const destinationId = item.folderId ?? null;
  if (draggedItem.type === 'folder' && draggedItem.id === destinationId) return;
  e.preventDefault();
 }

 async function handleBreadcrumbDrop(e, item) {
  e.preventDefault();
  if (!draggedItem || item.isDroppable === false) return;
  await moveItem(draggedItem, item.folderId ?? null);
  setDraggedItem(null);
 }

 async function moveItem(item, destinationFolderId) {
  const currentParent = isFolderView ? currentFolderId : null;
  if (destinationFolderId === currentParent) return; // já está lá, não-op
  try {
   if (item.type === 'folder') {
    await baseService.patch(`/document/folders/${item.id}/move`, {
     id_parent_folder: destinationFolderId,
    });
   } else {
    await baseService.patch(
     `/document/folders/${currentFolderId}/media/${item.id}/move`,
     { id_destination_folder: destinationFolderId }
    );
   }
   toast({ title: 'Movido com sucesso', status: 'success' });
   mutateFolders();
   mutateMedia();
  } catch (error) {
   toast({
    title: 'Não foi possível mover',
    description: error?.response?.data?.message,
    status: 'error',
   });
  }
 }

 // Área da grade também aceita soltar arquivo(s) do Windows Explorer pra
 // upload direto na pasta aberta — só quando NÃO é um drag interno de card
 // (draggedItem null) e só dentro de uma pasta de verdade.
 function handleGridDragOver(e) {
  if (draggedItem || !isFolderView) return;
  if (!e.dataTransfer.types.includes('Files')) return;
  e.preventDefault();
 }

 async function handleGridDrop(e) {
  if (draggedItem || !isFolderView) return;
  const file = e.dataTransfer.files?.[0];
  if (!file) return;
  e.preventDefault();
  await uploadFile(file);
 }

 // Lucas (22/09): tirar o botão "Voltar" solto do header (feio, ia sempre
 // pra /documents mesmo de dentro de uma pasta funda) — a navegação de
 // volta agora vive inteira no breadcrumb: Empresas > Documentação > ...
 // "Empresas" não é alvo de drop (não dá pra mover item pra outro espaço
 // por aqui) — isDroppable: false, ver handleBreadcrumbDrag*.
 // Modo global não tem crumb de "Documentação" — a pasta geral JÁ é o topo
 // desse espaço (getFolderPath já devolve ela como 1º item).
 const pathCrumbs = (breadcrumbPath || []).map((folder, index) => ({
  label: folder.name,
  folderId: folder.id_document_folder,
  isCurrentPage: index === breadcrumbPath.length - 1,
  onClick: index === breadcrumbPath.length - 1 ? undefined : () => openFolder(folder.id_document_folder),
 }));
 const breadcrumbItems = [
  { label: 'Empresas', folderId: null, isCurrentPage: false, isDroppable: false, onClick: () => navigate('/documents') },
  ...(isGlobal
   ? pathCrumbs
   : [
      {
       label: 'Documentação',
       folderId: null,
       isCurrentPage: isRoot,
       onClick: isRoot ? undefined : openRoot,
      },
      ...pathCrumbs,
     ]),
 ];

 if (isGlobal && !globalFolder) return <Loader />;
 if (loadingFolders) return <Loader />;

 // "Destacados" (Lucas, 21/09) — deixou de ser uma faixa sempre visível pra
 // virar toggle: ativo, a própria grade mostra só o que é favorito NESSA
 // pasta (puramente front, o dado já vem de /document/favorites).
 const visibleFolders = filterAndSort(
  showFavoritesOnly
   ? (folders || []).filter((f) => favoritedFolderIds.has(f.id_document_folder))
   : folders,
  nameSearch,
  sortBy,
  'created_at'
 );
 const visibleMedia = filterAndSort(
  showFavoritesOnly
   ? (media || []).filter((m) => favoritedMediaIds.has(m.id_media))
   : media,
  nameSearch,
  sortBy,
  'uploaded_at'
 );
 // Link não é favoritável: some no filtro de Destacados. Na busca/ordem, o
 // texto do botão conta como nome.
 const visibleLinks = showFavoritesOnly
  ? []
  : filterAndSort(
     (links || []).map((link) => ({ ...link, name: link.label })),
     nameSearch,
     sortBy,
     'created_at'
    );
 const isSearching = nameSearch.trim() !== '';

 return (
  <MainContent>
   <PageHeaderStructure
    title='Documentação'
    subtitle={isGlobal ? 'Visível para todas as empresas' : 'Pastas da empresa'}
   >
    {!isVirtual && !isReports && (
     <Button
      variant='primary'
      leftIcon={<FiFolderPlus />}
      onClick={() => {
       setEditingFolder(null);
       folderModal.onOpen();
      }}
     >
      {isFolderView ? 'Nova subpasta' : 'Nova pasta'}
     </Button>
    )}
   </PageHeaderStructure>

   <Box bg='white' borderRadius='12px' p='16px' mt='16px'>
    <Breadcrumbs
     items={breadcrumbItems}
     onItemDragOver={handleBreadcrumbDragOver}
     onItemDrop={handleBreadcrumbDrop}
    />

    {isFolderView && (
     <Flex justify='space-between' align='center' mb='16px'>
      <Flex align='center' gap='8px'>
       <Box>
        <Heading fontSize='18px'>{currentFolderData?.name}</Heading>
        {currentFolderData?.description && (
         <Text color='#5C6066' fontSize='sm' mt='4px'>
          {currentFolderData.description}
         </Text>
        )}
       </Box>
       {/* Documentos Gerais (is_system) é singleton fixo — sem renomear/excluir,
           mas o link direto vale pra ela também. Subpastas mantêm o menu todo. */}
       {currentFolderData && (
        <TableMenu
         menuItems={[
          {
           label: 'Copiar link para o cliente',
           handler: () => copyClientLink(currentFolderData.id_document_folder),
          },
          ...(currentFolderData.is_system
           ? []
           : [
              {
               label: 'Editar',
               handler: () => {
                setEditingFolder(currentFolderData);
                folderModal.onOpen();
               },
              },
              {
               label: 'Excluir',
               handler: () =>
                setDeleteTarget({ type: 'folder', id: currentFolderData.id_document_folder }),
              },
             ]),
         ]}
        />
       )}
      </Flex>
      <Flex gap='8px'>
       <Button size='sm' variant='outline' leftIcon={<FiLink />} onClick={() => openLinkModal()}>
        Adicionar link
       </Button>
       <Button
        size='sm'
        variant='primary'
        leftIcon={<FiUpload />}
        isLoading={isUploading}
        onClick={uploadModal.onOpen}
       >
        Enviar arquivo
       </Button>
      </Flex>
     </Flex>
    )}

    {isReports && (
     <Flex justify='space-between' align='center' mb='16px'>
      <Box>
       <Heading fontSize='18px'>Relatórios</Heading>
       <Text fontSize='sm' color='#818A8F'>
        Vinculados a um questionário ou certificação.
       </Text>
      </Box>
      <Button variant='primary' leftIcon={<FiUpload />} onClick={reportModal.onOpen}>
       Enviar relatório
      </Button>
     </Flex>
    )}

    {isVirtual && (
     <>
      <Heading fontSize='18px' mb='4px'>
       Questionários
      </Heading>
      <Text fontSize='sm' color='#818A8F' mb='16px'>
       Evidências enviadas pelo respondente — automática, somente leitura.
      </Text>
     </>
    )}

    {(isRoot || isFolderView) && (
     <>
      <Flex justify='space-between' align='center' gap='12px' mb='12px' wrap='wrap'>
       <Flex gap='12px' align='center' wrap='wrap'>
        <Input
         size='sm'
         w='260px'
         bg='white'
         borderRadius='8px'
         placeholder='Buscar por nome'
         value={nameSearch}
         onChange={(e) => setNameSearch(e.target.value)}
        />
        <Box w='180px'>
         <Select
          name='sortBy'
          height='32px'
          fontSize='sm'
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          options={SORT_OPTIONS}
         />
        </Box>
       </Flex>
       <Button
        size='sm'
        variant={showFavoritesOnly ? 'primary' : 'outline'}
        leftIcon={<FiStar color={showFavoritesOnly ? 'white' : '#F5A623'} fill={showFavoritesOnly ? 'white' : '#F5A623'} />}
        onClick={() => setShowFavoritesOnly((v) => !v)}
       >
        Destacados
       </Button>
      </Flex>

      <Box
       onDragOver={handleGridDragOver}
       onDrop={handleGridDrop}
       minH='120px'
      >
       <SimpleGrid columns={{ base: 2, md: 3, lg: 4, xl: 5 }} spacing='16px'>
        {isRoot && !showFavoritesOnly && !isSearching && (
         <>
          <Card onClick={openVirtual}>
           <Flex align='center' gap='10px' mb='8px'>
            <FolderCardIcon isSystem />
           </Flex>
           <Text fontSize='sm' fontWeight={600} noOfLines={1}>
            Questionários
           </Text>
           <Text fontSize='xs' color='#818A8F'>
            Automática · evidências enviadas
           </Text>
          </Card>
          <Card onClick={openReports}>
           <Flex align='center' gap='10px' mb='8px'>
            <FiFileText size={22} color='#818A8F' />
           </Flex>
           <Text fontSize='sm' fontWeight={600} noOfLines={1}>
            Relatórios
           </Text>
           <Text fontSize='xs' color='#818A8F'>
            Por questionário ou certificação
           </Text>
          </Card>
         </>
        )}

        {visibleFolders?.map((folder) => (
        <Card
         key={folder.id_document_folder}
         onClick={() => openFolder(folder.id_document_folder)}
         draggable
         onDragStart={(e) => handleDragStart(e, { type: 'folder', id: folder.id_document_folder })}
         onDragEnd={handleDragEnd}
         onDragOver={(e) => handleFolderDragOver(e, folder)}
         onDragLeave={() => setDropTarget(null)}
         onDrop={(e) => handleFolderDrop(e, folder)}
         isDropTarget={dropTarget === folder.id_document_folder}
        >
         <Flex justify='space-between' align='start'>
          <Flex align='center' gap='6px'>
           <FolderCardIcon />
           {favoritedFolderIds.has(folder.id_document_folder) && (
            <FiStar size={14} color='#F5A623' fill='#F5A623' />
           )}
          </Flex>
          <Box onClick={(e) => e.stopPropagation()}>
           <TableMenu
            menuItems={[
             {
              label: 'Editar',
              handler: () => {
               setEditingFolder(folder);
               folderModal.onOpen();
              },
             },
             {
              label: favoritedFolderIds.has(folder.id_document_folder) ? 'Desfavoritar' : 'Favoritar',
              handler: () => toggleFavorite('folder', folder.id_document_folder),
             },
             {
              label: 'Copiar link para o cliente',
              handler: () => copyClientLink(folder.id_document_folder),
             },
             {
              label: 'Excluir',
              handler: () => setDeleteTarget({ type: 'folder', id: folder.id_document_folder }),
             },
            ]}
           />
          </Box>
         </Flex>
         <Text fontSize='sm' fontWeight={600} noOfLines={1} mt='8px'>
          {folder.name}
         </Text>
         <Text fontSize='xs' color='#818A8F'>
          {folder.file_count} arquivo(s)
         </Text>
        </Card>
       ))}

       {isFolderView &&
        visibleMedia?.map((item) => (
         <Card
          key={item.id_document_folder_item}
          onClick={() => {
           setPreviewMedia(item);
           previewModal.onOpen();
          }}
          draggable
          onDragStart={(e) => handleDragStart(e, { type: 'media', id: item.id_media })}
          onDragEnd={handleDragEnd}
         >
          <Flex justify='space-between' align='start'>
           <Flex align='center' gap='6px'>
            {getFileIcon(item.name) ? (
             <img src={getFileIcon(item.name)} alt='' width='28' height='28' />
            ) : (
             <FiFileText size={22} color='#818A8F' />
            )}
            {favoritedMediaIds.has(item.id_media) && (
             <FiStar size={14} color='#F5A623' fill='#F5A623' />
            )}
           </Flex>
           <Box onClick={(e) => e.stopPropagation()}>
            <TableMenu
             menuItems={[
              {
               label: favoritedMediaIds.has(item.id_media) ? 'Desfavoritar' : 'Favoritar',
               handler: () => toggleFavorite('media', item.id_media),
              },
              { label: 'Baixar', handler: () => handleDownload(item.id_media) },
              {
               label: 'Copiar link para o cliente',
               handler: () => copyClientLink(currentFolderId, item.id_media),
              },
              {
               label: 'Excluir',
               handler: () => setDeleteTarget({ type: 'media', id: item.id_media }),
              },
             ]}
            />
           </Box>
          </Flex>
          <Text fontSize='sm' fontWeight={600} noOfLines={1} mt='8px'>
           {decodificarNomeDeUpload(item.name)}
          </Text>
          <Text fontSize='xs' color='#818A8F'>
           {new Date(item.uploaded_at).toLocaleDateString('pt-BR')}
          </Text>
         </Card>
        ))}

       {/* EG-70 — botão de link externo: azul fixo, texto escrito pelo admin. */}
       {isFolderView &&
        visibleLinks.map((link) => (
         <Card key={`link-${link.id_document_link}`}>
          <Flex justify='space-between' align='start'>
           <FiLink size={22} color='#2F80ED' />
           <TableMenu
            menuItems={[
             { label: 'Editar', handler: () => openLinkModal(link) },
             {
              label: 'Excluir',
              handler: () => setDeleteTarget({ type: 'link', id: link.id_document_link }),
             },
            ]}
           />
          </Flex>
          <Button
           as='a'
           href={link.url}
           target='_blank'
           rel='noopener noreferrer'
           size='sm'
           mt='8px'
           bg='#0073BB'
           color='white'
           _hover={{ bg: '#005F9A' }}
           rightIcon={<FiExternalLink />}
           whiteSpace='normal'
           h='auto'
           py='6px'
          >
           {link.label}
          </Button>
          <Text fontSize='xs' color='#818A8F' mt='6px' noOfLines={1}>
           {hostDoLink(link.url)}
          </Text>
         </Card>
        ))}
      </SimpleGrid>

       {isSearching && !loadingMedia && visibleFolders.length === 0 && visibleMedia.length === 0 && visibleLinks.length === 0 && (
        <Text fontSize='sm' color='#818A8F' mt='16px'>
         Nada encontrado com esse nome.
        </Text>
       )}
       {!isSearching && isRoot && !showFavoritesOnly && visibleFolders?.length === 0 && (
        <Text fontSize='sm' color='#818A8F' mt='16px'>
         Nenhuma pasta ainda.
        </Text>
       )}
       {!isSearching && showFavoritesOnly && visibleFolders?.length === 0 && visibleMedia?.length === 0 && (
        <Text fontSize='sm' color='#818A8F' mt='16px'>
         Nenhum favorito nessa pasta.
        </Text>
       )}
       {!isSearching && isFolderView && !showFavoritesOnly && !loadingMedia &&
        visibleFolders?.length === 0 && visibleMedia?.length === 0 && visibleLinks.length === 0 && (
        <Box mt='16px'>
         <Text fontSize='sm' color='#818A8F'>
          {'Pasta vazia. Arraste um arquivo aqui ou use "Enviar arquivo".'}
         </Text>
         <Text fontSize='xs' color='#B8BDC1' mt='4px'>
          Organize com subpastas — use "Nova subpasta" ali em cima.
         </Text>
        </Box>
       )}
       {isFolderView && loadingMedia && <Loader />}
      </Box>
     </>
    )}

    {isVirtual && (
     loadingVirtual ? (
      <Loader />
     ) : (
      <Table variant='simple' size='sm'>
       <Thead>
        <Tr>
         <Th>Arquivo</Th>
         <Th>Questão</Th>
         <Th>Questionário</Th>
         <Th w='80px'></Th>
        </Tr>
       </Thead>
       <Tbody>
        {virtualFiles?.length === 0 && (
         <Tr>
          <Td colSpan={4}>
           <Text color='#818A8F' py='4'>
            Nenhuma evidência enviada ainda.
           </Text>
          </Td>
         </Tr>
        )}
        {virtualFiles?.map((file) => (
         <Tr key={file.id_question_response_files}>
          <Td>{decodificarNomeDeUpload(file.file_name)}</Td>
          <Td>
           {file.question_code ? `${file.question_code} — ${file.question_title || ''}` : '—'}
          </Td>
          <Td>{file.survey_name || '—'}</Td>
          <Td>
           <Tooltip label='Baixar'>
            <Button
             size='xs'
             variant='ghost'
             onClick={() => handleDownloadVirtual(file.id_question_response_files)}
            >
             <FiDownload />
            </Button>
           </Tooltip>
          </Td>
         </Tr>
        ))}
       </Tbody>
      </Table>
     )
    )}

    {isReports && (
     loadingReports ? (
      <Loader />
     ) : (
      <Table variant='simple' size='sm'>
       <Thead>
        <Tr>
         <Th>Arquivo</Th>
         <Th>Vinculado a</Th>
         <Th>Enviado em</Th>
         <Th w='140px'></Th>
        </Tr>
       </Thead>
       <Tbody>
        {reports?.length === 0 && (
         <Tr>
          <Td colSpan={4}>
           <Text color='#818A8F' py='4'>
            Nenhum relatório enviado ainda.
           </Text>
          </Td>
         </Tr>
        )}
        {reports?.map((item) => (
         // Fase 9: 1 linha por VÍNCULO, não por mídia — a mesma mídia
         // reaproveitada em 2 questionários aparece 2x, então a key tem
         // que ser o vínculo (id_media_report_link), não item.id_media.
         <Tr key={item.id_media_report_link}>
          <Td>{decodificarNomeDeUpload(item.name)}</Td>
          <Td>{resolveReportLink(item)}</Td>
          <Td>{new Date(item.created_at).toLocaleDateString('pt-BR')}</Td>
          <Td>
           <Flex gap='8px'>
            <Tooltip label={favoritedMediaIds.has(item.id_media) ? 'Desfavoritar' : 'Favoritar'}>
             <Button size='xs' variant='ghost' onClick={() => toggleFavorite('media', item.id_media)}>
              <FiStar
               color={favoritedMediaIds.has(item.id_media) ? '#F5A623' : undefined}
               fill={favoritedMediaIds.has(item.id_media) ? '#F5A623' : 'none'}
              />
             </Button>
            </Tooltip>
            <Tooltip label='Baixar'>
             <Button size='xs' variant='ghost' onClick={() => handleDownload(item.id_media)}>
              <FiDownload />
             </Button>
            </Tooltip>
           </Flex>
          </Td>
         </Tr>
        ))}
       </Tbody>
      </Table>
     )
    )}
   </Box>

   <ReportUploadModal
    isOpen={reportModal.isOpen}
    onClose={reportModal.onClose}
    idCompanyGroup={idCompanyGroup}
    surveys={surveys}
    certificates={certificates}
    existingReports={reports}
    onUploaded={mutateReports}
   />

   <FolderModal
    isOpen={folderModal.isOpen}
    onClose={folderModal.onClose}
    idCompanyGroup={idCompanyGroup}
    folder={editingFolder}
    parentId={currentFolderId}
    onSaved={mutateFolders}
   />

   <LinkModal
    isOpen={linkModal.isOpen}
    onClose={linkModal.onClose}
    idFolder={currentFolderId}
    link={editingLink}
    onSaved={mutateLinks}
   />

   <UploadOrReuseModal
    isOpen={uploadModal.isOpen}
    onClose={uploadModal.onClose}
    idFolder={currentFolderId}
    spaceCompanyGroupId={spaceCompanyGroupId}
    onUploaded={() => {
     mutateMedia();
     mutateFolders();
    }}
   />

   <FilePreviewModal
    isOpen={previewModal.isOpen}
    onClose={previewModal.onClose}
    media={previewMedia}
    onDownload={handleDownload}
   />

   <AttentionModal
    isOpen={!!deleteTarget}
    onClose={() => setDeleteTarget(null)}
    submit={confirmDelete}
    redType
    title='Excluir'
    content={
     deleteTarget?.type === 'folder'
      ? 'Tem certeza que deseja excluir esta pasta?'
      : deleteTarget?.type === 'link'
      ? 'Tem certeza que deseja excluir este link?'
      : 'Tem certeza que deseja excluir este arquivo?'
    }
   />
  </MainContent>
 );
}
